/* eslint-disable no-unused-vars */
import cn from "classnames";
import { formatTime } from "@utils/player";
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { toFarsiNumber } from "@utils/digits";

interface Props {
  videoNode: HTMLVideoElement | null;
  fullScreen: boolean;
  onSeekTo: (t: number) => void;
}

export type ProgressBarElement = {
  updateDuration: (duration: number) => void;
  updateProgress: (time: number, duration: number, maskTime?: number) => void;
  updateBuffer: (time: number, duration: number) => void;
  seekTo: () => void;
  updateSeekData: (offset: number, duration: number) => void;
};

const ProgressBar = React.forwardRef<ProgressBarElement, Props>(
  ({ videoNode, fullScreen, onSeekTo }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const barRef = useRef<HTMLDivElement | null>(null);
    const maskRef = useRef<HTMLDivElement | null>(null);
    const bufferRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const barLeft = useRef(0);
    const progressLock = useRef(false);
    const mouseDown = useRef({ enabled: false, leftVal: 0 });
    const seekData = useRef("0");

    const handleSeekTo = useCallback(
      (fromControls: boolean = false) => {
        if (fromControls) {
          if (!progressLock.current) return;
        }
        if (!inputRef.current) return;
        const skipTo = seekData.current
          ? seekData.current
          : inputRef.current.value;
        inputRef.current.value = skipTo;
        progressLock.current = true;
        onSeekTo(+skipTo);
      },
      [onSeekTo],
    );
    const onDrag = (clientX: number, leftVal: number) => {
      if (!maskRef.current || !barRef.current || !scrollRef.current) return;

      barLeft.current = clientX - leftVal;

      if (barLeft.current < -8) barLeft.current = -8;
      else if (barLeft.current > scrollRef.current.offsetWidth - 8)
        barLeft.current = scrollRef.current.offsetWidth - 8;

      maskRef.current.style.width = barLeft.current + 8 + "px";
      barRef.current.style.left = barLeft.current + "px";

      window.getSelection
        ? window.getSelection()?.removeAllRanges()
        : document.selection.empty();
    };

    const onMouseUp = () => {
      if (tooltipRef.current) tooltipRef.current.style.display = "none";

      if (mouseDown.current.enabled) {
        mouseDown.current.enabled = false;
        handleSeekTo();
      }
    };

    const onMouseMove = (e: globalThis.MouseEvent) => {
      if (mouseDown.current.enabled) {
        const event = e || window.event;
        onDrag(event.clientX, mouseDown.current.leftVal);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (mouseDown.current.enabled) {
        const event = e || window.event;
        if (event.touches.length > 0)
          onDrag(event.touches[0].clientX, mouseDown.current.leftVal);
      }
    };
    useEffect(() => {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchend", onMouseUp);

      return () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("mouseup", onMouseUp);
        window.removeEventListener("touchend", onMouseUp);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleSeekTo]);

    const handleMouseMove = (
      event: MouseEvent<HTMLDivElement> | undefined,
      evenTouch: React.TouchEvent<HTMLDivElement> | undefined,
    ) => {
      if (!containerRef.current || !tooltipRef.current || !videoNode) return;

      const { target } = event ?? evenTouch ?? {};
      const isMouseEvent = typeof event !== "undefined";
      const isTouchEvent = typeof evenTouch !== "undefined";

      let offset = 0;
      let pageX = 0;

      if (isMouseEvent) {
        offset = event!.nativeEvent.offsetX;
        pageX = event!.nativeEvent.pageX;
      } else if (isTouchEvent) {
        if (evenTouch!.nativeEvent.touches.length === 0) return;
        const firstTouch = evenTouch!.nativeEvent.touches[0];
        offset = firstTouch.clientX;
        pageX = firstTouch.pageX;
      }

      if (target === barRef.current) {
        offset = (target as HTMLDivElement).offsetLeft + 8;
      }
      const clientWidth = containerRef.current.clientWidth;
      offset = Math.min(clientWidth, Math.max(offset, 0));

      const skipTo = Math.round((offset / clientWidth) * videoNode.duration);
      seekData.current = `${skipTo}`;

      const time = formatTime(skipTo);
      const { hours, minutes, seconds } = time;
      const timeDisplay =
        hours !== "00"
          ? `${toFarsiNumber(hours)}:${toFarsiNumber(minutes)}:${toFarsiNumber(
              seconds,
            )}`
          : `${toFarsiNumber(minutes)}:${toFarsiNumber(seconds)}`;

      tooltipRef.current.textContent = timeDisplay;

      const rect = videoNode.getBoundingClientRect();
      tooltipRef.current.style.left = `${pageX - rect.left}px`;
      tooltipRef.current.style.display = "block";
    };

    const updateProgress = (
      time: number,
      duration: number,
      maskTime?: number,
    ) => {
      if (!progressLock.current) {
        if (
          inputRef.current &&
          barRef.current &&
          maskRef.current &&
          containerRef.current
        ) {
          inputRef.current.value = Math.floor(time).toString();
          const progressStyle = maskRef.current.style;
          const timeWidth =
            (time / duration) * containerRef.current.clientWidth;
          progressStyle.width = timeWidth + "px";

          barRef.current.style.left = timeWidth - 8 + "px";
        }

        progressLock.current = false;
      } else if (
        typeof maskTime !== "undefined" &&
        barRef.current &&
        maskRef.current &&
        containerRef.current
      ) {
        maskRef.current.style.width =
          (maskTime / duration) * containerRef.current.clientWidth + "px";
        barRef.current.style.left =
          (time / duration) * containerRef.current.clientWidth - 8 + "px";
        progressLock.current = false;
      }
    };

    const updateSeekData = (offset: number, duration: number) => {
      if (!containerRef.current) return;
      const seek = containerRef.current;
      if (offset >= seek.clientWidth) {
        offset = seek.clientWidth;
      }
      const skipTo = Math.round((offset / seek.clientWidth) * duration);
      seekData.current = skipTo.toString();
    };

    const updateBuffer = (time: number, duration: number) => {
      if (bufferRef.current)
        bufferRef.current.style.width =
          Math.floor((time / duration) * 100) + "%";
    };
    const updateDuration = (duration: number) => {
      if (inputRef.current) {
        inputRef.current.max = duration.toString();
      }
    };
    useImperativeHandle(
      ref,
      () => ({
        updateDuration: updateDuration,
        updateProgress: updateProgress,
        updateBuffer: updateBuffer,
        seekTo: () => handleSeekTo(true),
        updateSeekData: updateSeekData,
      }),
      [handleSeekTo],
    );
    const keyDown = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
      progressLock.current = true;
      if (!barRef.current) return;

      const event = e || window.event;
      const leftVal = event.clientX - barRef.current.offsetLeft;
      mouseDown.current.enabled = true;
      mouseDown.current.leftVal = leftVal;
    };
    const touchStart = (e: React.TouchEvent<HTMLDivElement>) => {
      progressLock.current = true;
      if (!barRef.current) return;

      const event = e || window.event;

      if (event.touches.length > 0) {
        const leftVal = event.touches[0].clientX - barRef.current.offsetLeft;
        mouseDown.current.enabled = true;
        mouseDown.current.leftVal = leftVal;
      }
    };

    const mouseOut = () => {
      if (tooltipRef.current) {
        tooltipRef.current.style.display = "none";
      }
    };
    return (
      <div
        ref={containerRef}
        className={cn(
          "nplay-video-progress",
          fullScreen ? "msm:mb-8 " : "msm:mb-0",
        )}
      >
        <input
          ref={inputRef}
          className="seek hidden"
          defaultValue="0"
          min="0"
          type="range"
          step="1"
        />
        <div
          ref={scrollRef}
          className="nplay-scroll"
          onMouseMove={(e) => handleMouseMove(e, undefined)}
          onTouchMove={(e) => handleMouseMove(undefined, e)}
          onMouseLeave={mouseOut}
          onMouseOut={mouseOut}
          onClick={() => {
            handleSeekTo();
          }}
        >
          <div ref={bufferRef} className="nplay-buffer"></div>
          <div
            ref={barRef}
            className="nplay-bar"
            onMouseDown={keyDown}
            onTouchStart={touchStart}
          ></div>
          <div ref={maskRef} className="nplay-mask"></div>
        </div>
        <div ref={tooltipRef} className="seek-tooltip">
          ۰۰:۰۰
        </div>
      </div>
    );
  },
);
ProgressBar.displayName = "ProgressBar";

export default ProgressBar;
